/**
 * Mega Menu Navigation (Refactored)
 * - Stable hover behavior (no flicker / dead-zone closing)
 * - Event delegation (performance)
 * - Keyboard support (Escape, ArrowDown to enter submenu, Left/Right to move top items)
 * - Proper ARIA wiring (aria-expanded, aria-controls, aria-hidden)
 * - Shopify theme editor friendly (idempotent init)
 *
 * IMPORTANT:
 * This JS expects dropdown visibility to be controlled by an `.is-open` class.
 * If your CSS still uses `.mega-nav-item:hover .mega-menu-dropdown { ... }`,
 * remove that hover rule and add:
 *   .mega-menu-dropdown.is-open { opacity:1; visibility:visible; pointer-events:auto; transform:translateX(-50%) translateY(0); }
 *
 * Your current CSS *does* use hover to show dropdowns, which conflicts with JS and causes flicker. :contentReference[oaicite:0]{index=0}
 */

(() => {
  const SELECTORS = {
    navItem: '.mega-nav-item',
    navLink: '.mega-nav-link',
    dropdown: '.mega-menu-dropdown',
    blackout: '.mega-nav-blackout',
    section: '.mega-menu-navigation-section, .mega-nav-section', // supports both patterns
    focusableInDropdown:
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
  };

  const DESKTOP_BP = 989; // matches your existing breakpoint usage :contentReference[oaicite:1]{index=1}
  const OPEN_DELAY_MS = 60; // small intent delay prevents accidental opens
  const CLOSE_DELAY_MS = 200; // enough to cross into dropdown without flicker

  class MegaMenuNavigation {
    constructor(root = document) {
      this.root = root;

      // State
      this.activeItem = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.lastTrigger = null;

      // Cached elements
      this.blackout = document.querySelector(SELECTORS.blackout);

      // Media
      this.mqlDesktop = window.matchMedia(`(min-width: ${DESKTOP_BP + 1}px)`);

      // Idempotency
      if (window.__MegaMenuNavInitialized) return;
      window.__MegaMenuNavInitialized = true;

      this.init();
    }

    init() {
      this.setupAria();
      this.bindEvents();
      this.setupResizeHandler();
      this.ensureLiveRegion();
    }

    /* ---------- Accessibility / ARIA ---------- */

    setupAria() {
      const items = document.querySelectorAll(SELECTORS.navItem);

      items.forEach((item, idx) => {
        const link = item.querySelector(SELECTORS.navLink);
        const dropdown = item.querySelector(SELECTORS.dropdown);
        if (!link || !dropdown) return;

        // Ensure dropdown has an id so aria-controls can reference it
        if (!dropdown.id) dropdown.id = `mega-menu-dropdown-${idx + 1}`;

        // Treat top-level trigger as a disclosure trigger.
        // NOTE: In your old code you set role="button" on links, and role="menu/menuitem" in dropdown.
        // That's not ideal for a mega menu (it behaves like navigation, not an application menu). :contentReference[oaicite:2]{index=2}
        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-expanded', 'false');
        link.setAttribute('aria-controls', dropdown.id);

        dropdown.setAttribute('aria-hidden', 'true');
      });
    }

    ensureLiveRegion() {
      if (document.getElementById('mega-menu-live-region')) return;
      const el = document.createElement('div');
      el.id = 'mega-menu-live-region';
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('aria-atomic', 'true');
      // visually hidden
      el.style.position = 'absolute';
      el.style.width = '1px';
      el.style.height = '1px';
      el.style.margin = '-1px';
      el.style.border = '0';
      el.style.padding = '0';
      el.style.clip = 'rect(0 0 0 0)';
      el.style.overflow = 'hidden';
      document.body.appendChild(el);
    }

    announce(msg) {
      const region = document.getElementById('mega-menu-live-region');
      if (!region) return;
      region.textContent = '';
      // small delay to ensure SR announces repeated messages
      setTimeout(() => {
        region.textContent = msg;
      }, 10);
    }

    /* ---------- Event Binding (delegated) ---------- */

    bindEvents() {
      // Use pointer events for better cross-device behavior.
      document.addEventListener('pointerover', this.onPointerOver, true);
      document.addEventListener('pointerout', this.onPointerOut, true);

      // Click: mobile toggles, desktop optional (keeps keyboard users sane)
      document.addEventListener('click', this.onClick, true);

      // Keyboard
      document.addEventListener('keydown', this.onKeyDown, true);

      // Close on outside click
      document.addEventListener('click', (e) => {
        if (!e.target.closest(SELECTORS.navItem) && !e.target.closest(SELECTORS.dropdown)) {
          this.closeAll({ returnFocus: false });
        }
      });

      // Blackout click closes
      if (this.blackout) {
        this.blackout.addEventListener('click', () => this.closeAll({ returnFocus: true }));
      }
    }

    onPointerOver = (e) => {
      if (!this.isDesktop()) return;

      const item = e.target.closest(SELECTORS.navItem);
      if (!item) return;

      const dropdown = item.querySelector(SELECTORS.dropdown);
      if (!dropdown) return;

      // Entering item or dropdown should cancel close
      this.clearCloseTimer();

      // Hover intent open
      this.scheduleOpen(item, dropdown, e.target.closest(SELECTORS.navLink) || null);
    };

    onPointerOut = (e) => {
      if (!this.isDesktop()) return;

      // If pointer is moving within the same menu item/dropdown, ignore
      const leavingItem = e.target.closest(SELECTORS.navItem);
      if (!leavingItem) return;

      const dropdown = leavingItem.querySelector(SELECTORS.dropdown);
      if (!dropdown) return;

      const to = e.relatedTarget;
      const stillInside =
        (to && (leavingItem.contains(to) || dropdown.contains(to)));

      if (stillInside) return;

      // Schedule close to allow crossing small gaps
      this.scheduleClose(leavingItem);
    };

    onClick = (e) => {
      const columnTrigger = e.target.closest('[data-mega-column-toggle]');
      if (columnTrigger) {
        e.preventDefault();
        e.stopPropagation();
        const column = columnTrigger.closest('.mega-menu-column');
        const dropdown = columnTrigger.closest(SELECTORS.dropdown);
        const willOpen = !column?.classList.contains('is-expanded');

        dropdown?.querySelectorAll('.mega-menu-column.is-expanded').forEach((openColumn) => {
          if (openColumn === column) return;
          openColumn.classList.remove('is-expanded');
          openColumn.querySelector('[data-mega-column-toggle]')?.setAttribute('aria-expanded', 'false');
        });

        column?.classList.toggle('is-expanded', willOpen);
        columnTrigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        return;
      }

      const link = e.target.closest(SELECTORS.navLink);
      if (!link) return;

      const item = link.closest(SELECTORS.navItem);
      if (!item) return;

      const dropdown = item.querySelector(SELECTORS.dropdown);
      if (!dropdown) return;

      // Parent items behave as disclosure controls at every breakpoint.
      // The dropdown includes an explicit "View all" link for navigation.
      e.preventDefault();
      if (this.activeItem === item) {
        this.closeItem(item, { returnFocus: false });
      } else {
        this.openItem(item, dropdown, link, { focusFirst: false });
      }
    };

    onKeyDown = (e) => {
      const activeEl = document.activeElement;
      const currentLink = activeEl?.closest?.(SELECTORS.navLink);
      const currentItem = activeEl?.closest?.(SELECTORS.navItem);

      // Esc closes any open dropdown and returns focus to trigger
      if (e.key === 'Escape') {
        if (this.activeItem) {
          e.preventDefault();
          this.closeAll({ returnFocus: true });
        }
        return;
      }

      // Keyboard navigation on top-level triggers
      if (currentLink && currentItem) {
        const dropdown = currentItem.querySelector(SELECTORS.dropdown);

        // Enter/Space toggles submenu (desktop + mobile)
        if ((e.key === 'Enter' || e.key === ' ') && dropdown) {
          e.preventDefault();
          if (this.activeItem === currentItem) {
            this.closeItem(currentItem, { returnFocus: true });
          } else {
            this.openItem(currentItem, dropdown, currentLink, { focusFirst: true });
          }
          return;
        }

        // ArrowDown opens submenu and focuses first item
        if (e.key === 'ArrowDown' && dropdown) {
          e.preventDefault();
          this.openItem(currentItem, dropdown, currentLink, { focusFirst: true });
          return;
        }

        // ArrowRight/Left move between top-level items
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const items = Array.from(document.querySelectorAll(SELECTORS.navItem))
            .filter(i => i.querySelector(SELECTORS.navLink));
          const idx = items.indexOf(currentItem);
          if (idx === -1) return;

          const nextIdx =
            e.key === 'ArrowRight'
              ? (idx + 1) % items.length
              : (idx - 1 + items.length) % items.length;

          const nextLink = items[nextIdx].querySelector(SELECTORS.navLink);
          nextLink?.focus();

          // If a menu is open, follow focus to that menu item
          if (this.activeItem) {
            const nextDropdown = items[nextIdx].querySelector(SELECTORS.dropdown);
            if (nextDropdown) this.openItem(items[nextIdx], nextDropdown, nextLink, { focusFirst: false });
            else this.closeAll({ returnFocus: false });
          }
          return;
        }
      }

      // Keyboard navigation within dropdown (ArrowUp/Down) without trapping Tab
      const dropdown = activeEl?.closest?.(SELECTORS.dropdown);
      if (dropdown) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const focusables = Array.from(dropdown.querySelectorAll(SELECTORS.focusableInDropdown))
            .filter(el => el.offsetParent !== null);

          if (!focusables.length) return;

          const idx = focusables.indexOf(activeEl);
          const nextIdx =
            e.key === 'ArrowDown'
              ? (idx + 1) % focusables.length
              : (idx - 1 + focusables.length) % focusables.length;

          focusables[nextIdx]?.focus();
        }

        // Close the menu if focus moves out naturally (Tab)
        // We do not trap focus; mega menus are navigation, not modal dialogs.
        if (e.key === 'Tab') {
          // Let Tab proceed, but schedule a close if focus leaves the dropdown & trigger.
          this.clearCloseTimer();
          setTimeout(() => {
            if (!this.activeItem) return;
            const active = document.activeElement;
            const trigger = this.activeItem.querySelector(SELECTORS.navLink);
            const dd = this.activeItem.querySelector(SELECTORS.dropdown);
            const stillInside = (trigger && trigger.contains(active)) || (dd && dd.contains(active));
            if (!stillInside) this.closeAll({ returnFocus: false });
          }, 0);
        }
      }
    };

    /* ---------- Open / Close Logic ---------- */

    isDesktop() {
      return this.mqlDesktop.matches;
    }

    scheduleOpen(item, dropdown, link) {
      this.clearOpenTimer();
      this.openTimer = setTimeout(() => {
        this.openItem(item, dropdown, link, { focusFirst: false });
      }, OPEN_DELAY_MS);
    }

    scheduleClose(item) {
      this.clearOpenTimer();
      this.clearCloseTimer();
      this.closeTimer = setTimeout(() => {
        // only close if pointer isn't currently over trigger or dropdown
        if (this.activeItem === item) {
          const trigger = item.querySelector(SELECTORS.navLink);
          const dropdown = item.querySelector(SELECTORS.dropdown);
          const hovered = document.querySelector(':hover');
          const stillHovering =
            (trigger && trigger.matches(':hover')) ||
            (dropdown && dropdown.matches(':hover')) ||
            (dropdown && dropdown.contains(hovered)) ||
            (trigger && trigger.contains(hovered));

          if (!stillHovering) this.closeItem(item, { returnFocus: false });
        }
      }, CLOSE_DELAY_MS);
    }

    openItem(item, dropdown, link, { focusFirst }) {
      if (!item || !dropdown) return;

      // Close current first (one open at a time)
      if (this.activeItem && this.activeItem !== item) {
        this.closeItem(this.activeItem, { returnFocus: false });
      }

      this.activeItem = item;
      this.lastTrigger = link || item.querySelector(SELECTORS.navLink);

      item.classList.add('is-open');

      // CSS class drives visibility (no inline style thrash)
      dropdown.classList.add('is-open');
      dropdown.classList.add('mega-menu-fade-in');
      dropdown.classList.remove('mega-menu-fade-out');

      // Desktop: show blackout (if present)
      if (this.blackout && this.isDesktop()) {
        this.blackout.classList.add('active');
      }

      // ARIA
      if (this.lastTrigger) this.lastTrigger.setAttribute('aria-expanded', 'true');
      dropdown.setAttribute('aria-hidden', 'false');

      if (focusFirst) {
        const first = dropdown.querySelector(
          '.mega-menu-column-title, .mega-menu-link, .mega-menu-promo, a[href], button:not([disabled])'
        );
        first?.focus();
      }

      this.announce('Submenu opened');
    }

    closeItem(item, { returnFocus }) {
      if (!item) return;
      const dropdown = item.querySelector(SELECTORS.dropdown);
      const link = item.querySelector(SELECTORS.navLink);

      if (dropdown) {
        dropdown.classList.remove('is-open');
        dropdown.classList.add('mega-menu-fade-out');
        dropdown.classList.remove('mega-menu-fade-in');
        dropdown.setAttribute('aria-hidden', 'true');
      }

      item.classList.remove('is-open');
      item.querySelectorAll('.mega-menu-column.is-expanded').forEach((column) => {
        column.classList.remove('is-expanded');
        column.querySelector('[data-mega-column-toggle]')?.setAttribute('aria-expanded', 'false');
      });

      if (link) link.setAttribute('aria-expanded', 'false');

      if (this.blackout) this.blackout.classList.remove('active');

      if (returnFocus && link) link.focus();

      if (this.activeItem === item) this.activeItem = null;
    }

    closeAll({ returnFocus }) {
      this.clearOpenTimer();
      this.clearCloseTimer();

      const openDropdowns = document.querySelectorAll(`${SELECTORS.dropdown}.is-open`);
      openDropdowns.forEach(dd => {
        const item = dd.closest(SELECTORS.navItem);
        if (item) this.closeItem(item, { returnFocus: false });
      });

      if (returnFocus && this.lastTrigger) {
        this.lastTrigger.focus();
      }

      this.activeItem = null;
    }

    clearOpenTimer() {
      if (this.openTimer) {
        clearTimeout(this.openTimer);
        this.openTimer = null;
      }
    }

    clearCloseTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    /* ---------- Performance / Resilience ---------- */

    setupResizeHandler() {
      let t = null;
      window.addEventListener(
        'resize',
        () => {
          if (t) clearTimeout(t);
          t = setTimeout(() => {
            // On mode switch, close everything (prevents stuck state)
            this.closeAll({ returnFocus: false });
          }, 150);
        },
        { passive: true }
      );
    }
  }

  // Init on DOMContentLoaded
  const init = () => new MegaMenuNavigation(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: safe re-init
  document.addEventListener('shopify:section:load', () => {
    // No-op if already initialized (idempotent)
    if (!window.__MegaMenuNavInitialized) new MegaMenuNavigation(document);
  });
})();
